import {
  type Instant,
  type LocalDate,
  type Money,
  formatLocalDate,
  fromApiMoney,
  parseInstant,
  parseLocalDate,
  toApiMoney,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { BinaryResponse, TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type ReceiptScanJobStatus = Schemas['ReceiptScanJobResponse']['status']
export type ReceiptFailureCode = NonNullable<Schemas['ReceiptScanJobResponse']['failureCode']>

export interface ReceiptLineItem {
  readonly description: string
  readonly amount: Money
  readonly quantity: number
  /** This line's own expense category - unset until extraction's suggestion is accepted or the
   * reviewer picks one. Falls back to [ReceiptScanJob.counterAccountId] at commit time. */
  readonly accountId: string | undefined
  readonly isDiscount: boolean
}

/** [status] `NEEDS_REVIEW` means the line items still do not sum to [statedTotal] after every
 * automatic re-analysis attempt; `commit` re-checks that sum itself regardless of [status]. */
export interface ReceiptScanJob {
  readonly id: string
  readonly accountId: string
  readonly counterAccountId: string | undefined
  readonly currency: string
  readonly imageContentType: string
  readonly status: ReceiptScanJobStatus
  readonly attemptCount: number
  /** `undefined` unless [status] is `FAILED` - the server's own stable discriminator, never the
   * raw internal failure text (that stays server-side, logged only). */
  readonly failureCode: ReceiptFailureCode | undefined
  readonly merchant: string | undefined
  readonly receiptDate: LocalDate | undefined
  readonly statedTotal: Money | undefined
  readonly lineItems: readonly ReceiptLineItem[]
  readonly postedEntryId: string | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
  readonly committedAt: Instant | undefined
}

export interface CreateReceiptScanInput {
  readonly accountId: string
  readonly file: File
}

export interface EditReceiptExtractionLineItem {
  readonly description: string
  readonly amount: Money
  readonly quantity: number
  readonly accountId: string | undefined
  readonly isDiscount: boolean
}

export interface EditReceiptExtractionInput {
  readonly counterAccountId: string | undefined
  readonly merchant: string | undefined
  readonly receiptDate: LocalDate | undefined
  readonly statedTotal: Money | undefined
  readonly lineItems: readonly EditReceiptExtractionLineItem[]
}

/** [lineIndex] is the line's position in [ReceiptScanJob.lineItems]; [confidence] is a decimal
 * string in `0`..`1`, same convention as every other ratio on the wire. */
export interface ReceiptCategorySuggestion {
  readonly lineIndex: number
  readonly accountId: string
  readonly confidence: string
}

export const receiptScanKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'receipt-scans', 'list'),
  detail: (ledgerId: string, jobId: string): CacheKey =>
    ledgerKey(ledgerId, 'receipt-scans', 'detail', jobId),
  /** Committing also posts a journal entry, so the journal and any reporting read must be
   * treated as stale too - the same reasoning as `snapshotKeys.invalidatedByWrite`. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [
    ledgerKey(ledgerId, 'receipt-scans'),
    ledgerKey(ledgerId, 'entries'),
    ledgerKey(ledgerId, 'reporting'),
  ],
  invalidatedByDelete: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'receipt-scans')],
} as const

export interface ReceiptScansRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly ReceiptScanJob[]>
  get(ledgerId: string, jobId: string, signal?: AbortSignal): Promise<ReceiptScanJob>
  create(ledgerId: string, input: CreateReceiptScanInput): Promise<ReceiptScanJob>
  /** The review step's correction - a full replacement of every editable field. */
  editExtraction(ledgerId: string, jobId: string, input: EditReceiptExtractionInput): Promise<ReceiptScanJob>
  /** Moves a `FAILED` or `NEEDS_REVIEW` job back through extraction against the same image. */
  rescan(ledgerId: string, jobId: string): Promise<ReceiptScanJob>
  commit(ledgerId: string, jobId: string): Promise<ReceiptScanJob>
  /** Proposes a category per uncategorised line item - read-only, confirmed only via [editExtraction]/[applyCategorySuggestions]. */
  categorySuggestions(
    ledgerId: string,
    jobId: string,
    signal?: AbortSignal,
  ): Promise<readonly ReceiptCategorySuggestion[]>
  /** Writes every suggestion at or above [minConfidence] (a decimal string in `0`..`1`) through [editExtraction]. */
  applyCategorySuggestions(ledgerId: string, jobId: string, minConfidence: string): Promise<ReceiptScanJob>
  /** Only a `FAILED` job may be deleted - a successful scan never offers this. */
  delete(ledgerId: string, jobId: string): Promise<void>
  /** The stored photo, fetched through the client so the bearer token reaches the server -
   * never a bare `<img src>` link (same discipline as `ExportsRepository.download`). */
  image(ledgerId: string, jobId: string, signal?: AbortSignal): Promise<BinaryResponse>
}

export function createReceiptScansRepository(client: TalerClient): ReceiptScansRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/receipt-scans`
  const one = (ledgerId: string, jobId: string) => `${base(ledgerId)}/${encodeURIComponent(jobId)}`

  return {
    async list(ledgerId, signal) {
      const jobs = await client.call<Schemas['ReceiptScanJobResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return jobs.map(toReceiptScanJob)
    },

    async get(ledgerId, jobId, signal) {
      return toReceiptScanJob(
        await client.call<Schemas['ReceiptScanJobResponse']>({
          method: 'GET',
          path: one(ledgerId, jobId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      const formData = new FormData()
      formData.append('file', input.file, input.file.name)
      return toReceiptScanJob(
        await client.callMultipart<Schemas['ReceiptScanJobResponse']>({
          method: 'POST',
          path: base(ledgerId),
          query: { accountId: input.accountId },
          formData,
          idempotencyPayload: {
            accountId: input.accountId,
            filename: input.file.name,
            size: input.file.size,
            lastModified: input.file.lastModified,
          },
        }),
      )
    },

    async editExtraction(ledgerId, jobId, input) {
      return toReceiptScanJob(
        await client.call<Schemas['ReceiptScanJobResponse'], Schemas['EditReceiptExtractionRequest']>({
          method: 'PATCH',
          path: one(ledgerId, jobId),
          body: {
            ...(input.counterAccountId === undefined ? {} : { counterAccountId: input.counterAccountId }),
            ...(input.merchant === undefined ? {} : { merchant: input.merchant }),
            ...(input.receiptDate === undefined ? {} : { receiptDate: formatLocalDate(input.receiptDate) }),
            ...(input.statedTotal === undefined ? {} : { statedTotal: apiMoney(input.statedTotal) }),
            lineItems: input.lineItems.map((item) => ({
              description: item.description,
              amount: apiMoney(item.amount),
              quantity: item.quantity,
              ...(item.accountId === undefined ? {} : { accountId: item.accountId }),
              isDiscount: item.isDiscount,
            })),
          },
        }),
      )
    },

    async rescan(ledgerId, jobId) {
      return toReceiptScanJob(
        await client.call<Schemas['ReceiptScanJobResponse'], Record<string, never>>({
          method: 'POST',
          path: `${one(ledgerId, jobId)}/rescan`,
          body: {},
        }),
      )
    },

    async commit(ledgerId, jobId) {
      return toReceiptScanJob(
        await client.call<Schemas['ReceiptScanJobResponse'], Record<string, never>>({
          method: 'POST',
          path: `${one(ledgerId, jobId)}/commit`,
          body: {},
        }),
      )
    },

    async categorySuggestions(ledgerId, jobId, signal) {
      const suggestions = await client.call<Schemas['ReceiptCategorySuggestionResponse'][]>({
        method: 'GET',
        path: `${one(ledgerId, jobId)}/category-suggestions`,
        ...(signal === undefined ? {} : { signal }),
      })
      return suggestions.map((item) => ({
        lineIndex: item.lineIndex,
        accountId: item.accountId,
        confidence: item.confidence,
      }))
    },

    async applyCategorySuggestions(ledgerId, jobId, minConfidence) {
      return toReceiptScanJob(
        await client.call<Schemas['ReceiptScanJobResponse'], Schemas['ApplyCategorySuggestionsRequest']>({
          method: 'POST',
          path: `${one(ledgerId, jobId)}/category-suggestions/apply`,
          body: { minConfidence },
        }),
      )
    },

    async delete(ledgerId, jobId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, jobId) })
    },

    async image(ledgerId, jobId, signal) {
      return client.callBinary({
        path: `${one(ledgerId, jobId)}/image`,
        ...(signal === undefined ? {} : { signal }),
      })
    },
  }
}

/** See `snapshots.ts`'s identical helper: `@taler/domain`'s currency list is wider than the wire enum. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

function toReceiptScanJob(response: Schemas['ReceiptScanJobResponse']): ReceiptScanJob {
  return {
    id: response.id,
    accountId: response.accountId,
    counterAccountId: response.counterAccountId,
    currency: response.currency,
    imageContentType: response.imageContentType,
    status: response.status,
    attemptCount: response.attemptCount,
    failureCode: response.failureCode,
    merchant: response.merchant,
    receiptDate: response.receiptDate === undefined ? undefined : parseLocalDate(response.receiptDate),
    statedTotal: response.statedTotal === undefined ? undefined : fromApiMoney(response.statedTotal),
    lineItems: response.lineItems.map((item) => ({
      description: item.description,
      amount: fromApiMoney(item.amount),
      quantity: item.quantity,
      accountId: item.accountId,
      isDiscount: item.isDiscount,
    })),
    postedEntryId: response.postedEntryId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
    committedAt: response.committedAt === undefined ? undefined : parseInstant(response.committedAt),
  }
}
